import { useMemo, useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Field, Input, Kpi, PageHead, useToast } from '@w7/ui'
import { cmApi, type EventSearchHit } from '@/api/cmApi'
import { useCmMe } from '@/api/hooks/useCmMe'
import { authHeaders } from '@/auth/session'
import { canAccessUiPath } from '@/app/staffAccess'
import { formatInr, fromMinorUnits } from '@/features/hierarchy/hierarchy'

export function HomePage() {
  const navigate = useNavigate()
  const toast = useToast()
  const me = useCmMe()
  const [q, setQ] = useState('')
  const showMarketSelection = canAccessUiPath(me.data?.caller, '/catalog/market-selection')

  const money = useQuery({
    queryKey: ['cm', 'hierarchy', 'self'],
    queryFn: () => cmApi.hierarchySelf(authHeaders()),
  })

  const summary = useQuery({
    queryKey: ['cm', 'portal-summary'],
    queryFn: () => cmApi.portalSummary(authHeaders()),
  })

  const search = useMutation({
    mutationFn: (query: string) => cmApi.eventSearch(authHeaders(), query, 25),
    onError: (err: Error) => toast.error(err.message),
  })

  const sports = summary.data?.sports ?? []
  const hits: EventSearchHit[] = search.data?.events ?? []

  const onSearch = (e: FormEvent) => {
    e.preventDefault()
    const trimmed = q.trim()
    if (!trimmed) {
      toast.error('Enter an event name or Betfair id')
      return
    }
    search.mutate(trimmed)
  }

  const kpiBusy = summary.isLoading
  const moneyBusy = money.isLoading
  const m = money.data?.money
  const node = money.data?.node

  const sportLabel = useMemo(() => {
    if (!sports.length) return undefined
    return sports.map((s) => `${s.name} ${s.eventCount}`).join(' · ')
  }, [sports])

  return (
    <div className="cm-page">
      <PageHead
        title="Company Master"
        subtitle="Manual and automated Market-API catalog ops via CM-API"
        actions={
          <>
            <Button type="button" variant="primary" onClick={() => navigate('/catalog/manual')}>
              Manual catalog
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate('/catalog/automation')}>
              Automation
            </Button>
          </>
        }
      />

      <div className="w7-grid w7-grid-4 cm-home-kpis" data-testid="money-kpis">
        <Kpi
          label="Live balance"
          value={
            moneyBusy
              ? '—'
              : formatInr(fromMinorUnits(m?.liveBalance ?? node?.liveBalance ?? node?.balance))
          }
          meta="TigerBeetle cash on CM"
        />
        <Kpi
          label="Total balance"
          value={
            moneyBusy
              ? '—'
              : formatInr(fromMinorUnits(m?.totalBalance ?? node?.totalBalance ?? node?.creditLimit))
          }
          meta="Credit line on this CM"
        />
        <Kpi
          label="Distributed balance"
          value={
            moneyBusy
              ? '—'
              : formatInr(
                  fromMinorUnits(
                    m?.distributedBalance ?? money.data?.allocatedCredit ?? node?.distributedBalance,
                  ),
                )
          }
          meta={
            moneyBusy
              ? undefined
              : `${formatInr(fromMinorUnits(m?.remainingToAssign ?? money.data?.availableCredit))} remaining to assign`
          }
        />
        <Kpi
          label="Open exposure"
          value={
            moneyBusy ? '—' : formatInr(fromMinorUnits(m?.exposure ?? node?.exposure))
          }
          meta="Your share of player bets"
        />
      </div>

      {money.isError ? (
        <p className="cm-hint" role="alert">
          Could not load CM wallet: {(money.error as Error).message}
        </p>
      ) : null}

      <div className="w7-grid w7-grid-4 cm-home-kpis" data-testid="portal-kpis">
        <Kpi label="Sports on portal" value={kpiBusy ? '—' : String(summary.data?.totalSports ?? 0)} />
        <Kpi
          label="Upcoming events"
          value={kpiBusy ? '—' : String(summary.data?.totalUpcoming ?? 0)}
        />
        <Kpi
          label="In-play events"
          value={kpiBusy ? '—' : String(summary.data?.totalInPlay ?? 0)}
        />
        <Kpi
          label="Events by sport"
          value={kpiBusy ? '—' : String(sports.reduce((n, s) => n + s.eventCount, 0))}
          meta={sportLabel}
        />
      </div>

      {summary.isError ? (
        <p className="cm-hint" role="alert">
          Could not load portal counts: {(summary.error as Error).message}
        </p>
      ) : null}

      {sports.length ? (
        <div className="cm-sport-counts" data-testid="sport-counts">
          {sports.map((s) => (
            <div key={s.sportId} className="cm-sport-count">
              <strong>{s.name}</strong>
              <span>
                {s.eventCount} events · {s.upcomingCount} upcoming · {s.inPlayCount} in-play
              </span>
            </div>
          ))}
        </div>
      ) : null}

      <form className="cm-home-search" onSubmit={onSearch}>
        <Field label="Search events (name or Betfair id)">
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={q}
              onChange={(ev) => setQ(ev.target.value)}
              placeholder="e.g. Arsenal or 34223314"
              data-testid="event-search-input"
            />
          )}
        </Field>
        <Button type="submit" variant="primary" busy={search.isPending}>
          Search
        </Button>
      </form>

      {hits.length ? (
        <div className="cm-search-results" data-testid="event-search-results">
          {hits.map((hit) => (
            <button
              key={hit.eventId}
              type="button"
              className="cm-search-row"
              onClick={() =>
                navigate(
                  `/catalog/manual?eventId=${encodeURIComponent(hit.vendorEventId)}&sportHint=${encodeURIComponent(hit.sportName)}`,
                )
              }
            >
              <strong>{hit.name}</strong>
              <span>
                {hit.sportName}
                {hit.leagueName ? ` · ${hit.leagueName}` : ''} · BF {hit.vendorEventId}
                {hit.inPlay ? ' · in-play' : ''}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      <div className="cm-home-grid">
        <button type="button" className="cm-home-card" onClick={() => navigate('/net-exposure')}>
          <strong>Net Exposure</strong>
          <span>Open event books · My PT / Total Book · Detail Book</span>
        </button>
        <button type="button" className="cm-home-card" onClick={() => navigate('/hierarchy')}>
          <strong>Portal hierarchy</strong>
          <span>Downline tree · credit limits · give / take</span>
        </button>
        <button type="button" className="cm-home-card" onClick={() => navigate('/hierarchy/list')}>
          <strong>Downline list</strong>
          <span>Full flat list · search · edit direct children</span>
        </button>
        <button
          type="button"
          className="cm-home-card"
          onClick={() => navigate('/hierarchy/create')}
        >
          <strong>Create Triple Master</strong>
          <span>Assign credit, Member Rate / My share, fancy/BM commission</span>
        </button>
        <button type="button" className="cm-home-card" onClick={() => navigate('/catalog/manual')}>
          <strong>Manual catalog</strong>
          <span>1) Fetch events → 2) Fetch markets → 3) Enable / automation</span>
        </button>
        <button type="button" className="cm-home-card" onClick={() => navigate('/catalog/browse')}>
          <strong>Browse catalog</strong>
          <span>League → event → markets tables (then fetch / flag automation)</span>
        </button>
        <button
          type="button"
          className="cm-home-card"
          onClick={() => navigate('/catalog/automation')}
        >
          <strong>Automation</strong>
          <span>Auto-fetch on/off · enable window in hours (e.g. 24 = next day)</span>
        </button>
        <button
          type="button"
          className="cm-home-card"
          onClick={() => navigate('/catalog/betfair-fetch')}
        >
          <strong>Betfair Market Fetch</strong>
          <span>Search exchange, fetch markets (multi), update start time</span>
        </button>
        {showMarketSelection ? (
          <button
            type="button"
            className="cm-home-card"
            onClick={() => navigate('/catalog/market-selection')}
          >
            <strong>Market selection</strong>
            <span>Match Odds · fancy · session · bookmaker · limits · scorecard</span>
          </button>
        ) : null}
        <button
          type="button"
          className="cm-home-card"
          onClick={() => navigate('/bet-settings/max-limits')}
        >
          <strong>Max Limits</strong>
          <span>Sport-level min/max bet · fancy &amp; bookmaker caps</span>
        </button>
        <button
          type="button"
          className="cm-home-card"
          onClick={() => navigate('/bet-settings/volume-percent')}
        >
          <strong>Volume Percent New</strong>
          <span>TM place % / min stake for place bet &amp; mock bet</span>
        </button>
        <button
          type="button"
          className="cm-home-card"
          onClick={() => navigate('/logs/betfair-rates-logs')}
        >
          <strong>Betfair Rates Logs</strong>
          <span>Who changed TM place % / min stake, from which IP</span>
        </button>
        <button type="button" className="cm-home-card" onClick={() => navigate('/surveillance')}>
          <strong>Surveillance</strong>
          <span>
            Settled exchange bets on the odds timeline — ≤7 days ending today via
            Postgres, else BigQuery
          </span>
        </button>
      </div>
    </div>
  )
}

/** Old routes → new catalog pages. */
export function LegacyFetchRedirect({ to }: { to: string }) {
  return <Navigate to={to} replace />
}
